import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import RwdBasketMain from 'components/RwdBasket/RwdBasketMain/RwdBasketMain';
import RwdPreBasket from 'components/RwdBasket/RwdPreBasket/RwdPreBasket';
import SetPageAnalyticsProps from 'components/Analytics';
import SavedForLaterCarousel from 'components/SaveForLater/SavedForLaterCarousel';
import { ROOT_BASKET_TYPES, MAIN_BASKET_TYPES } from 'constants/RwdBasket';
import anaConsts from 'analytics/constants';
import { fireRoktPageView } from 'utils/adUtils';
import UI from 'utils/UI';
import Empty from 'constants/empty';

class RwdBasketRoot extends BaseClass {
    componentDidMount() {
        this.props.resetScrollToTop();
        // If Save For Later is enabled and user is signed-in, fetch the list on init
        const { showSaveForLater, getSaveForLaterLists, userId, isInitialized } = this.props;

        if (showSaveForLater && userId && getSaveForLaterLists) {
            getSaveForLaterLists(userId);
        }

        if (isInitialized) {
            this.fireRoktBasketPageView();
        }
    }

    componentDidUpdate(prevProps) {
        const { isInitialized, currentRootBasketType, currentMainBasketType } = this.props;

        if (isInitialized) {
            if (
                !prevProps.isInitialized ||
                prevProps.currentRootBasketType !== currentRootBasketType ||
                prevProps.currentMainBasketType !== currentMainBasketType
            ) {
                this.fireRoktBasketPageView();
            }
        }

        if (prevProps.currentMainBasketType !== currentMainBasketType) {
            this.props.resetScrollToTop();
        }

        // Fire loaded event when basket is available and changes
        const { cartInfo, basket } = this.props;
        const prevBasket = prevProps.basket;

        if (isInitialized && cartInfo && basket && basket !== prevBasket) {
            this.handleCartLoaded(basket);
        }

        const prevUserId = prevProps.userId;
        const currUserId = this.props.userId;
        const { showSaveForLater, getSaveForLaterLists } = this.props;

        // Fetch when flag flips to true or user signs in after mount while flag is true
        if (showSaveForLater && currUserId && getSaveForLaterLists) {
            if (prevProps.showSaveForLater !== showSaveForLater || !prevUserId) {
                getSaveForLaterLists(currUserId);
            }
        }
    }
    componentWillUnmount() {
        this.props.resetNavigation();
    }

    fireRoktBasketPageView() {
        const { currentRootBasketType, currentMainBasketType } = this.props;
        let title;

        if (currentRootBasketType === ROOT_BASKET_TYPES.PRE_BASKET) {
            title = 'Basket - Mixed (both)';
        } else if (currentMainBasketType === MAIN_BASKET_TYPES.BOPIS_BASKET) {
            title = 'Basket - BOPIS';
        } else {
            title = 'Basket (standard)';
        }

        fireRoktPageView({ pagetype: 'basket', screenname: title, title });
    }

    handleCartLoaded(basket) {
        try {
            Sephora.logger.verbose(`P13N: Cart Loaded ${JSON.stringify(basket)}`);
        } catch (error) {
            Sephora.logger.error('[RwdBasketRoot] Error in handleCartLoaded:', error);
        }
    }

    buildSaveForLaterCarousel() {
        const {
            showSaveForLater, saveForLaterCarouselData, saveForLaterLoading, getSaveForLaterLists, userId
        } = this.props;

        if (!showSaveForLater) {
            return Empty.Array;
        }

        const isLoading = saveForLaterLoading;
        const hasData = saveForLaterCarouselData?.skuList?.length > 0;

        if (!isLoading && !hasData) {
            return Empty.Array;
        }

        return [
            <SavedForLaterCarousel
                key='saved-for-later-carousel'
                saveForLaterCarouselData={saveForLaterCarouselData}
                saveForLaterLoading={saveForLaterLoading}
                getSaveForLaterLists={getSaveForLaterLists}
                userId={userId}
            />
        ];
    }

    render() {
        const {
            currentRootBasketType,
            currentMainBasketType,
            goToPickUpBasket,
            goToDCBasket,
            goToPreBasket,
            cartInfo,
            cartComponentLists,
            paymentInfo,
            messageInfo,
            isPreBasketAvailable,
            shouldScrollToTop,
            biBenefitsInfo,
            giftCardInfo,
            topContentMessageComponentList,
            preBasketInfoModalCallbacks,
            bottomContentComponentList,
            isInitialized,
            refreshBasket,
            swapGiftCardBiBenefitBasket,
            showBasketGreyBackground,
            showMoveBasketCheckoutButton,
            showSaveForLater,
            oosBiOrSample,
            isInventoryAwareStorePickerEnabled
        } = this.props;

        if (!isInitialized) {
            return null;
        }

        // Wrap in explicit if to avoid calling scrollToTop on every render
        if (shouldScrollToTop) {
            UI.scrollToTop();
        }

        // Build Save For Later carousel if the feature is enabled and the list contains items
        const savedForLaterCarouselItems = this.buildSaveForLaterCarousel();

        if (currentRootBasketType === ROOT_BASKET_TYPES.PRE_BASKET) {
            return (
                <>
                    <SetPageAnalyticsProps
                        pageType={anaConsts.PAGE_TYPES.BASKET}
                        pageName={anaConsts.PAGE_NAMES.PREBASKET}
                        world={null}
                    />
                    <RwdPreBasket
                        goToPickUpBasket={goToPickUpBasket}
                        goToDCBasket={goToDCBasket}
                        cartInfo={cartInfo}
                        paymentInfo={paymentInfo}
                        messageInfo={messageInfo[ROOT_BASKET_TYPES.PRE_BASKET]}
                        preBasketInfoModalCallbacks={preBasketInfoModalCallbacks}
                        isInventoryAwareStorePickerEnabled={isInventoryAwareStorePickerEnabled}
                    />
                </>
            );
        }

        if (currentRootBasketType === ROOT_BASKET_TYPES.MAIN_BASKET) {
            // TODO get rid of RwdBasketMain and just switch between two instances of RwdBasketLayout
            return (
                <>
                    <SetPageAnalyticsProps
                        pageType={anaConsts.PAGE_TYPES.BASKET}
                        pageName={anaConsts.PAGE_NAMES.BASKET}
                    />
                    <RwdBasketMain
                        goToPickUpBasket={goToPickUpBasket}
                        goToDCBasket={goToDCBasket}
                        goToPreBasket={goToPreBasket}
                        cartComponentLists={cartComponentLists}
                        basketType={currentMainBasketType}
                        isPreBasketAvailable={isPreBasketAvailable}
                        cartInfo={cartInfo}
                        paymentInfo={paymentInfo}
                        messageInfo={messageInfo[ROOT_BASKET_TYPES.MAIN_BASKET]}
                        topContentMessageComponentList={topContentMessageComponentList}
                        biBenefitsInfo={biBenefitsInfo}
                        giftCardInfo={giftCardInfo}
                        bottomContentComponentList={bottomContentComponentList}
                        refreshBasket={refreshBasket}
                        swapGiftCardBiBenefitBasket={swapGiftCardBiBenefitBasket}
                        showBasketGreyBackground={showBasketGreyBackground}
                        showMoveBasketCheckoutButton={showMoveBasketCheckoutButton}
                        showSaveForLater={showSaveForLater}
                        savedForLaterCarouselItems={savedForLaterCarouselItems}
                        oosBiOrSample={oosBiOrSample}
                    />
                </>
            );
        }

        return null;
    }
}

export default wrapComponent(RwdBasketRoot, 'RwdBasketRoot', true);
